/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-magic-numbers */

import { produce } from 'immer';
import { v4 as uuidv4 } from 'uuid';
import { StateCreator } from 'zustand';

import { ModuleStats, PracticeContentResponse, PracticeStats } from '@/hooks/api/usePractice';
import logger from '@/logger';
import { ModulePlayerStatsType } from '@/pages/ModulePlayer/hooks/useModulePlayer';
import {
  AttemptInfo,
  ExplanationStats,
  HintStats,
  NarrationStats,
  QuestionType,
} from '@/pages/TaskPlayer/types';
import { getValidatedDataForPracticeCueingEngine } from '@/pages/TaskPlayer/validationUtil';
import { AppState } from '@/slices/appSlice';
import { StageState } from '@/slices/stageSlice';
import { TestState } from '@/slices/testSlice';

export const MAX_QUESTIONS_ALLOWED = 3;
const MIN_QUESTIONS_FOR_FAIL = 3;
const PASS_THRESHOLD = 2 / 3;
const getScoreRange = (
  questionStats: PracticeQuestionStats[],
  availableQuestions: QuestionType[],
) => {
  const totalAsked = questionStats.length;
  const remainingCount = Math.min(MAX_QUESTIONS_ALLOWED - totalAsked, availableQuestions.length);
  const correctCount = questionStats.filter((stat) => stat.isCorrect).length;
  return {
    minScore: correctCount / (totalAsked + remainingCount),
    maxScore: (correctCount + remainingCount) / (totalAsked + remainingCount),
    score: correctCount / totalAsked,
    remainingCount,
  };
};

const selectRandomQuestion = (questions: QuestionType[]) => {
  return questions[Math.floor(Math.random() * questions.length)];
};

const isCurrentSetOver = (
  questionStats: PracticeQuestionStats[],
  availableQuestions: QuestionType[],
) => {
  const { /* minScore, maxScore,  */ remainingCount, score } = getScoreRange(
    questionStats,
    availableQuestions,
  );
  return {
    currentSetOver:
      remainingCount === 0 /* || minScore >= PASS_THRESHOLD || maxScore < PASS_THRESHOLD */,
    score,
  };
};

const precompute = (getPracticeContentResponse: PracticeContentResponse) => {
  return getValidatedDataForPracticeCueingEngine(getPracticeContentResponse);
};

interface BasicQuestionStats {
  questionId: string;
  format: string;
  medium: string;
  seqNo: number;
  isCorrect: boolean;
  translationViewed: string[];
  type: number;
  explanationStats?: ExplanationStats[];
  hintStats?: HintStats[];
  difficulty: number;
  narration?: NarrationStats;
}

export interface PracticeQuestionStats extends BasicQuestionStats, AttemptInfo {}

export interface PracticeState {
  /** api response related state */
  getPracticeContentResponse?: PracticeContentResponse;

  /** stats related state */
  currentPracticeQuestionStat?: PracticeQuestionStats;
  skillWiseQuestionStats: { [skillId: string]: PracticeQuestionStats[] };
  currentSkillQuestionStats: PracticeQuestionStats[];
  skillWiseModuleStats: { [skillId: string]: ModuleStats[] };
  skillWiseQuestionsTimeStats: {
    [skillId: string]: { duration: number; startTime: string; endTime: string };
  };
  practiceAttemptIds: { [skillId: string]: string };
  currentPracticeAttemptId: string | undefined;
  beforeAttemptHintStats: HintStats[];

  /** practice journey related state */
  showPracticeModules: boolean;
  practiceStatsAvailableFrom?: string;

  /** api execution related state */
  submitPracticeResultRetryCount: number;

  /** narration tracking (per question) */
  currentNarrationStats: { completed: boolean; playCount: number };

  /** logic related state */
  currentPracticeSkillIndex: number;

  /** api response parsing related behaviour */
  initialisePractice: ({
    getPracticeContentResponse,
  }: {
    getPracticeContentResponse: PracticeContentResponse;
  }) => void;

  /** narration related behaviour (Practice) */
  resetCurrentNarrationStatsPractice: () => void;
  reportNarrationReached90Practice: () => void;

  /** stats related behaviour */
  storeUserAttemptPractice: ({
    attemptInfo,
    hintUsed,
    isCorrect,
    medium,
  }: {
    attemptInfo: AttemptInfo;
    isCorrect: boolean;
    hintUsed: number;
    medium: string;
  }) => void;
  setPracticeHintStats: (newStat: HintStats) => void;
  setPracticeExplanationStats: (newStat: ExplanationStats) => void;

  /** practice journey related behaviour */
  setShowPracticeModules: (showPracticeModules: boolean) => void;
  setCurrentPracticeAttemptId: (attemptId: string | undefined) => void;
  clearPracticeState: () => void;
  setPracticeStatsAvailableFrom: (source?: string) => void;

  /** api execution related behaviour */
  incrementSubmitPracticeResultRetryCount: () => void;
  getSubmitPracticeResultRetryCount: () => number;

  /** logic related behaviour */
  moveToNextSkill: () => void;
  processUserAnswerPractice: (currentSkillQuestionTimeStats: {
    duration: number;
    startTime: string;
    endTime: string;
  }) => boolean;
  getAvailableQuestionsForPractice: () => QuestionType[];
  setModuleStats: (moduleStats: ModulePlayerStatsType) => void;
  getPracticeStats: () => PracticeStats | undefined;
}

const getInitialState = () => {
  return {
    /** api response related state */
    getPracticeContentResponse: undefined,

    /** stats related state */
    currentPracticeQuestionStat: undefined,
    skillWiseQuestionStats: {},
    currentSkillQuestionStats: [],
    skillWiseModuleStats: {},
    skillWiseQuestionsTimeStats: {},
    practiceAttemptIds: {},
    currentPracticeAttemptId: undefined,
    beforeAttemptHintStats: [],

    /** practice journey related state */
    showPracticeModules: false,
    practiceStatsAvailableFrom: undefined,

    /** api execution related state */
    submitPracticeResultRetryCount: 0,

    /** narration tracking */
    currentNarrationStats: { completed: false, playCount: 0 },

    /** logic related state */
    question: undefined,
    currentPracticeSkillIndex: 0,
  };
};

const getInititalTranslationViewed = (
  currentQuestion: QuestionType | undefined,
  mediumPriority: string[],
) => {
  if (currentQuestion && mediumPriority) {
    // eslint-disable-next-line unicorn/prefer-array-find
    const availableLanguages = mediumPriority.filter(
      (medium) => currentQuestion.mediumMapping[medium],
    );
    return [availableLanguages[0]];
  }
  return [];
};

export const createPracticeSlice: StateCreator<
  TestState & StageState & PracticeState & AppState,
  [['zustand/devtools', never]],
  [],
  PracticeState
> = (set, get) => ({
  ...getInitialState(),

  /** api response parsing related behaviour */
  initialisePractice: ({
    getPracticeContentResponse,
  }: {
    getPracticeContentResponse: PracticeContentResponse;
  }) =>
    set(
      (_state) =>
        produce(_state, (state: PracticeState & StageState) => {
          const { content, errors, isValid } = precompute(getPracticeContentResponse);
          if (!isValid) {
            const info = [
              'Mode Practice ',
              'Level id ',
              _state.currentLevel,
              'Mission id ',
              _state.currentMission?.id,
              'Mission no ',
              _state.currentMission?.missionNo,
            ];
            logger.info(info.join(' '));
          }
          if (isValid) {
            state.warnings = errors;
            state.getPracticeContentResponse = { ...getPracticeContentResponse, content };
            state.currentPracticeSkillIndex = 0;
            state.practiceAttemptIds = Object.fromEntries(
              content.map((skill) => [skill.skillId, uuidv4()]),
            );
            state.currentPracticeAttemptId = state.practiceAttemptIds[content[0].skillId];
            state.question = selectRandomQuestion(content[0].questions);
            state.translationViewed = getInititalTranslationViewed(
              state.question,
              getPracticeContentResponse.mediumPriority,
            );
            state.skillWiseQuestionStats = {};
            state.currentSkillQuestionStats = [];
            state.skillWiseQuestionsTimeStats = {};
            state.currentNarrationStats = { completed: false, playCount: 0 };
          } else {
            state.errors = errors;
          }
        }),
      false,
      'practiceSlice/initialisePractice',
    ),

  resetCurrentNarrationStatsPractice: () =>
    set(
      { currentNarrationStats: { completed: false, playCount: 0 } },
      false,
      'practiceSlice/resetCurrentNarrationStatsPractice',
    ),
  reportNarrationReached90Practice: () =>
    set(
      (_state) =>
        produce(_state, (state: PracticeState & StageState) => {
          const newPlayCount = state.currentNarrationStats.playCount + 1;
          state.currentNarrationStats = { completed: true, playCount: newPlayCount };
          const lastIndex = state.currentSkillQuestionStats.length - 1;
          if (lastIndex >= 0 && state.currentSkillQuestionStats[lastIndex]?.narration?.enabled) {
            state.currentSkillQuestionStats[lastIndex].narration = {
              enabled: true,
              completed: true,
              playCount: newPlayCount,
            };
          }
        }),
      false,
      'practiceSlice/reportNarrationReached90Practice',
    ),

  /** stats related behaviour */
  storeUserAttemptPractice: ({
    attemptInfo,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    hintUsed,
    isCorrect,
    medium,
  }: {
    attemptInfo: AttemptInfo;
    isCorrect: boolean;
    hintUsed: number;
    medium: string;
  }) =>
    set(
      (_state) =>
        produce(_state, (state: PracticeState & StageState) => {
          if (!state.currentSkillQuestionStats) {
            state.currentSkillQuestionStats = [];
          }
          if (!state.currentPracticeQuestionStat) {
            state.currentPracticeQuestionStat = {} as PracticeQuestionStats;
          }
          const {
            currentSkillQuestionStats,
            currentPracticeQuestionStat,
            question,
            translationViewed,
            currentNarrationStats,
          } = state;
          const { answer, startTime, endTime, duration } = attemptInfo;
          if (!question) {
            return;
          }
          const voiceover = question.mediumMapping[medium]?.metadata?.voiceover;
          const narrationEnabled = voiceover?.type === 'audio' && !!voiceover?.value;
          const narration: NarrationStats = {
            enabled: narrationEnabled,
            completed: narrationEnabled ? currentNarrationStats.completed : false,
            playCount: narrationEnabled ? currentNarrationStats.playCount : 0,
          };

          currentPracticeQuestionStat.answer = answer;
          currentPracticeQuestionStat.medium = medium;
          currentPracticeQuestionStat.isCorrect = isCorrect;
          currentPracticeQuestionStat.duration = duration;
          currentPracticeQuestionStat.endTime = endTime;
          currentPracticeQuestionStat.narration = narration;

          currentPracticeQuestionStat.questionId = question.id;
          currentPracticeQuestionStat.type = question.type;
          currentPracticeQuestionStat.format = question.format;
          currentPracticeQuestionStat.difficulty = question.difficulty;
          currentPracticeQuestionStat.startTime = startTime;
          currentPracticeQuestionStat.seqNo = currentSkillQuestionStats.length + 1;
          currentPracticeQuestionStat.translationViewed = translationViewed;

          const { beforeAttemptHintStats } = state;
          if (beforeAttemptHintStats && beforeAttemptHintStats.length > 0) {
            const existingHintStats = currentPracticeQuestionStat.hintStats || [];
            const mergedHintStats = [...existingHintStats];

            for (const beforeAttemptHint of beforeAttemptHintStats) {
              const existingIndex = mergedHintStats.findIndex(
                (hint) =>
                  hint.hintIndex === beforeAttemptHint.hintIndex &&
                  hint.type === beforeAttemptHint.type,
              );

              if (existingIndex >= 0) {
                // Merge with existing hint stats
                const existingHint = mergedHintStats[existingIndex];
                mergedHintStats[existingIndex] = {
                  ...beforeAttemptHint,
                  viewTime: existingHint.viewTime + beforeAttemptHint.viewTime,
                  viewCount: existingHint.viewCount + beforeAttemptHint.viewCount,
                };
              } else {
                // Add new hint stats
                mergedHintStats.push(beforeAttemptHint);
              }
            }

            if (mergedHintStats.length > 0) {
              currentPracticeQuestionStat.hintStats = mergedHintStats;
            }
            // Clear before-attempt hint stats after merging
            state.beforeAttemptHintStats = [];
          }

          currentSkillQuestionStats.push(currentPracticeQuestionStat);
        }),
      false,
      'practiceSlice/storeUserAttemptPractice',
    ),

  /** practice journey related behaviour */
  setShowPracticeModules: (showPracticeModules: boolean) =>
    set({ showPracticeModules }, false, 'practiceSlice/setShowPracticeModules'),
  clearPracticeState: () => {
    get().clearSharedState();
    set({ ...getInitialState() }, false, 'practiceSlice/clearPracticeState');
  },
  setCurrentPracticeAttemptId: (currentPracticeAttemptId: string | undefined) =>
    set({ currentPracticeAttemptId }, false, 'practiceSlice/setCurrentPracticeAttemptId'),
  setPracticeStatsAvailableFrom: (practiceStatsAvailableFrom?: string) =>
    set({ practiceStatsAvailableFrom }, false, 'practiceSlice/setPracticeStatsAvailableFrom'),

  /** api execution related behaviour */
  incrementSubmitPracticeResultRetryCount: () =>
    set((state) => ({
      submitPracticeResultRetryCount: state.submitPracticeResultRetryCount + 1,
    })),
  getSubmitPracticeResultRetryCount: () => get().submitPracticeResultRetryCount,

  /** logic related behaviour */
  moveToNextSkill: () =>
    set(
      (_state) =>
        produce(_state, (state: PracticeState & StageState) => {
          const { currentPracticeSkillIndex, getPracticeContentResponse } = get();
          if (!getPracticeContentResponse) return;
          const contentLength = getPracticeContentResponse?.content?.length ?? 0;
          if (currentPracticeSkillIndex === contentLength - 1) {
            state.practiceStatsAvailableFrom = 'lastSkill';
          } else if (currentPracticeSkillIndex < contentLength - 1) {
            state.currentPracticeSkillIndex = currentPracticeSkillIndex + 1;
            state.showPracticeModules = false;
            state.question = selectRandomQuestion(
              getPracticeContentResponse.content[currentPracticeSkillIndex + 1].questions,
            );
            state.currentNarrationStats = { completed: false, playCount: 0 };
          }
        }),
      false,
      'practiceSlice/moveToNextSkill',
    ),

  processUserAnswerPractice: (currentSkillQuestionTimeStats: {
    duration: number;
    startTime: string;
    endTime: string;
  }) => {
    let skillQuestionsCompleted = false;
    set(
      (_state) =>
        produce(_state, (state: PracticeState & StageState) => {
          const {
            currentSkillQuestionStats,
            currentPracticeSkillIndex,
            getPracticeContentResponse,
            getAvailableQuestionsForPractice,
          } = state;
          if (!getPracticeContentResponse) return;
          const { skillId, modules, questions } =
            getPracticeContentResponse.content[currentPracticeSkillIndex];
          const availableQuestions = getAvailableQuestionsForPractice();
          const { currentSetOver, score } = isCurrentSetOver(
            currentSkillQuestionStats,
            availableQuestions,
          );

          const { beforeAttemptHintStats } = state;
          if (beforeAttemptHintStats.length > 0 && currentSkillQuestionStats.length > 0) {
            const lastIndex = currentSkillQuestionStats.length - 1;
            const currentQuestionStat = currentSkillQuestionStats[lastIndex];
            const existingHintStats = currentQuestionStat.hintStats || [];
            const mergedHintStats = [...existingHintStats];

            for (const beforeAttemptHint of beforeAttemptHintStats) {
              const existingIndex = mergedHintStats.findIndex(
                (hint) =>
                  hint.hintIndex === beforeAttemptHint.hintIndex &&
                  hint.type === beforeAttemptHint.type,
              );

              if (existingIndex >= 0) {
                const existingHint = mergedHintStats[existingIndex];
                mergedHintStats[existingIndex] = {
                  ...beforeAttemptHint,
                  viewTime: existingHint.viewTime + beforeAttemptHint.viewTime,
                  viewCount: existingHint.viewCount + beforeAttemptHint.viewCount,
                };
              } else {
                mergedHintStats.push(beforeAttemptHint);
              }
            }

            currentSkillQuestionStats[lastIndex].hintStats = mergedHintStats;
            state.beforeAttemptHintStats = [];
          }

          if (currentSetOver) {
            skillQuestionsCompleted = true;
            // eslint-disable-next-line unicorn/consistent-destructuring
            state.skillWiseQuestionsTimeStats[skillId] = currentSkillQuestionTimeStats;
            // eslint-disable-next-line unicorn/consistent-destructuring
            state.skillWiseQuestionStats[skillId] = [...currentSkillQuestionStats];
            state.currentSkillQuestionStats = [];
            if (
              score < PASS_THRESHOLD &&
              questions.length >= MIN_QUESTIONS_FOR_FAIL &&
              modules.length > 0
            ) {
              state.showPracticeModules = true;
              state.currentModuleIndex = 0;
            } else if (currentPracticeSkillIndex < getPracticeContentResponse.content.length - 1) {
              state.currentPracticeSkillIndex += 1;
              state.question = selectRandomQuestion(
                getPracticeContentResponse.content[currentPracticeSkillIndex + 1].questions,
              );
            } else {
              state.practiceStatsAvailableFrom = 'lastSkill';
            }
          } else {
            state.question = selectRandomQuestion(availableQuestions);
          }
          state.currentNarrationStats = { completed: false, playCount: 0 };
          state.translationViewed = getInititalTranslationViewed(
            // eslint-disable-next-line unicorn/consistent-destructuring
            state.question,
            getPracticeContentResponse?.mediumPriority,
          );
          state.currentPracticeQuestionStat = undefined;
        }),
      false,
      'practiceSlice/processUserAnswerPractice',
    );
    return skillQuestionsCompleted;
  },
  getAvailableQuestionsForPractice: () => {
    const { getPracticeContentResponse, currentPracticeSkillIndex } = get();
    if (getPracticeContentResponse) {
      const { questions } = getPracticeContentResponse.content[currentPracticeSkillIndex];
      const attemptedQuestionIds = new Set(
        [
          ...Object.values(get().skillWiseQuestionStats).flat(),
          ...get().currentSkillQuestionStats,
        ].map((stat) => stat.questionId),
      );

      return questions?.filter((q) => !attemptedQuestionIds.has(q.id)) || [];
    }
    return [];
  },
  setModuleStats: (moduleStats: ModulePlayerStatsType) =>
    set(
      (_state) =>
        produce(_state, (state: PracticeState) => {
          const { currentPracticeSkillIndex, getPracticeContentResponse } = state;
          if (getPracticeContentResponse) {
            const { skillId } = getPracticeContentResponse.content[currentPracticeSkillIndex];
            // eslint-disable-next-line unicorn/consistent-destructuring
            if (!state.skillWiseModuleStats[skillId]) state.skillWiseModuleStats[skillId] = [];

            const newEntry = {
              moduleId: moduleStats.moduleId,
              attemptId: moduleStats.attemptId,
              moduleType: moduleStats.moduleType,
              metadata: {
                ...moduleStats.metadata,
              },
              moduleCategory: moduleStats.moduleCategory,
            };

            // eslint-disable-next-line unicorn/consistent-destructuring
            const existingIndex = state.skillWiseModuleStats[skillId].findIndex(
              (m) => m.moduleId === moduleStats.moduleId,
            );

            if (existingIndex >= 0) {
              // eslint-disable-next-line unicorn/consistent-destructuring
              const existing = state.skillWiseModuleStats[skillId][existingIndex];
              const existingPauseTime =
                'pauseTime' in existing.metadata ? existing.metadata.pauseTime : 0;
              const newPauseTime =
                'pauseTime' in newEntry.metadata ? newEntry.metadata.pauseTime : 0;
              // eslint-disable-next-line unicorn/consistent-destructuring
              state.skillWiseModuleStats[skillId][existingIndex] = {
                ...newEntry,
                metadata: {
                  ...newEntry.metadata,
                  viewTime: (existing.metadata.viewTime || 0) + (newEntry.metadata.viewTime || 0),
                  ...((existingPauseTime || newPauseTime) && {
                    pauseTime: (existingPauseTime || 0) + (newPauseTime || 0),
                  }),
                  completion: Math.max(
                    existing.metadata.completion || 0,
                    newEntry.metadata.completion || 0,
                  ),
                  startTime: existing.metadata.startTime,
                  endTime: newEntry.metadata.endTime,
                },
              };
            } else {
              // eslint-disable-next-line unicorn/consistent-destructuring
              state.skillWiseModuleStats[skillId].push(newEntry);
            }
          }
        }),
      false,
      'practiceSlice/setModuleStats',
    ),
  getPracticeStats: () => {
    const {
      currentMission,
      getPracticeContentResponse,
      skillWiseQuestionStats,
      skillWiseModuleStats,
      skillWiseQuestionsTimeStats,
      practiceAttemptIds,
    } = get();
    let finalResult;
    if (getPracticeContentResponse && currentMission) {
      finalResult = {
        missionId: currentMission.id,
        practiceAttemptNo: getPracticeContentResponse.practiceAttemptNo,
        skillStats: getPracticeContentResponse.content
          .filter((skill) => skillWiseQuestionStats[skill.skillId]?.length)
          .map((skill) => {
            const attemptId = practiceAttemptIds[skill.skillId];
            return {
              skillId: skill.skillId,
              strategyId: skill.strategyId,
              questionStats: {
                attemptId,
                totalDuration: skillWiseQuestionsTimeStats[skill.skillId].duration || 0,
                startTime: skillWiseQuestionsTimeStats[skill.skillId].startTime || '',
                endTime: skillWiseQuestionsTimeStats[skill.skillId].endTime || '',
                stats: skillWiseQuestionStats[skill.skillId] || [],
              },
              moduleStats: skillWiseModuleStats[skill.skillId] || [],
            };
          }),
      };
    }
    return finalResult;
  },
  setPracticeHintStats: (newStat: HintStats) =>
    set(
      (_state) =>
        produce(_state, (state: PracticeState) => {
          if (state.currentPracticeQuestionStat) {
            const lastIndex = state.currentSkillQuestionStats.length - 1;
            const existingHintStats = state.currentSkillQuestionStats[lastIndex].hintStats || [];
            const existingIndex = existingHintStats.findIndex(
              (hint) => hint.hintIndex === newStat.hintIndex && hint.type === newStat.type,
            );

            if (existingIndex >= 0) {
              const existingHint = existingHintStats[existingIndex];
              existingHintStats[existingIndex] = {
                ...newStat,
                viewTime: existingHint.viewTime + newStat.viewTime,
                viewCount: existingHint.viewCount + 1,
              };
            } else {
              existingHintStats.push({
                ...newStat,
                viewCount: 1,
              });
            }

            if (existingHintStats.length > 0) {
              state.currentSkillQuestionStats[lastIndex].hintStats = existingHintStats;
            }
          } else {
            const existingHintStats = state.beforeAttemptHintStats || [];
            const existingIndex = existingHintStats.findIndex(
              (hint) => hint.hintIndex === newStat.hintIndex && hint.type === newStat.type,
            );
            if (existingIndex >= 0) {
              const existingHint = existingHintStats[existingIndex];
              existingHintStats[existingIndex] = {
                ...newStat,
                viewTime: existingHint.viewTime + newStat.viewTime,
                viewCount: existingHint.viewCount + 1,
              };
            } else {
              existingHintStats.push({
                ...newStat,
                viewCount: 1,
              });
            }

            state.beforeAttemptHintStats = existingHintStats;
          }
        }),
      false,
      'practiceSlice/setPracticeHintStats',
    ),
  setPracticeExplanationStats: (newStat: ExplanationStats) =>
    set(
      (_state) =>
        produce(_state, (state: PracticeState) => {
          if (state.currentSkillQuestionStats.length > 0) {
            const lastIndex = state.currentSkillQuestionStats.length - 1;
            if (state.currentSkillQuestionStats[lastIndex]) {
              const existingExplanationStats =
                state.currentSkillQuestionStats[lastIndex].explanationStats || [];
              const existingIndex = existingExplanationStats.findIndex(
                (explanation) =>
                  explanation.explanationIndex === newStat.explanationIndex &&
                  explanation.type === newStat.type,
              );

              if (existingIndex >= 0) {
                const existingExplanation = existingExplanationStats[existingIndex];
                existingExplanationStats[existingIndex] = {
                  ...newStat,
                  metadata: {
                    ...newStat.metadata,
                    viewTime: existingExplanation.metadata.viewTime + newStat.metadata.viewTime,
                    viewCount: existingExplanation.metadata.viewCount + 1,
                    ...(newStat.metadata.pauseTime && {
                      pauseTime:
                        (existingExplanation.metadata.pauseTime || 0) + newStat.metadata.pauseTime,
                    }),
                    ...(newStat.metadata.completion >= 0 && {
                      completion: Math.max(
                        existingExplanation.metadata.completion || 0,
                        newStat.metadata.completion,
                      ),
                    }),
                    ...(newStat.metadata?.pageViewCount !== undefined && {
                      pageViewCount: Math.max(
                        existingExplanation.metadata.pageViewCount || 0,
                        newStat.metadata.pageViewCount,
                      ),
                    }),
                    ...(newStat.metadata.totalPageCount && {
                      totalPageCount: newStat.metadata.totalPageCount,
                    }),
                  },
                };
              } else {
                existingExplanationStats.push({
                  ...newStat,
                  metadata: {
                    ...newStat.metadata,
                    viewCount: 1,
                  },
                });
              }

              if (existingExplanationStats.length > 0) {
                state.currentSkillQuestionStats[lastIndex].explanationStats =
                  existingExplanationStats;
              }
            }
          }
        }),
      false,
      'practiceSlice/setPracticeExplanationStats',
    ),
});
